import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import api from '../api/api';
import { RestaurantAttendance, RestaurantEmployment } from '../types/restaurantPersonnel';
import './SelfAttendance.css';

const errorMessage = (error: unknown, fallback: string) => {
  if (axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message)
    return error.response.data.message;
  return fallback;
};

const SelfAttendance: React.FC<{ onChanged?: () => Promise<void> }> = ({ onChanged }) => {
  const [enabled, setEnabled] = useState(false);
  const [openShift, setOpenShift] = useState<RestaurantAttendance | null>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const pending = useRef(false);

  const refresh = async (signal?: AbortSignal) => {
    setReady(false);
    try {
      const summary = await api.get<{ employment: RestaurantEmployment; openAttendance: RestaurantAttendance | null }>(
        '/restaurant-personnel/me', { signal });
      if (signal?.aborted) return false;
      setEnabled(summary.data.employment.useAttendance);
      setOpenShift(summary.data.openAttendance);
      setError('');
      setReady(true);
      return true;
    } catch (failure: unknown) {
      if (!signal?.aborted) {
        setError(errorMessage(failure, 'Не вдалося перевірити стан табелю. Оновіть стан перед наступною дією.'));
      }
      return false;
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    void refresh(controller.signal);
    return () => controller.abort();
  }, []);

  const mark = async (kind: 'check-in' | 'check-out') => {
    if (pending.current || !ready || !enabled) return;
    pending.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await api.post(`/restaurant-personnel/me/${kind}`);
      setMessage(kind === 'check-in' ? 'Прихід зафіксовано.' : 'Вихід зафіксовано.');
      await refresh();
      await onChanged?.();
    } catch (failure: unknown) {
      // A lost response is not proof that attendance was not recorded.
      await refresh();
      setError(errorMessage(failure, 'Не вдалося підтвердити дію. Перевірте оновлений табель перед повторною спробою.'));
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };

  return (
    <section className="self-attendance" aria-label="Моє табелювання" aria-busy={busy}>
      <h2>Моє табелювання</h2>
      {!ready && !error && <p role="status">Перевірка табелю…</p>}
      {error && <p role="alert">{error}</p>}
      {message && <p role="status">{message}</p>}
      {ready && (
        <p>{!enabled ? 'Табелювання для вас вимкнено керівником.'
          : openShift ? `Зміна відкрита з ${new Date(openShift.checkInAt).toLocaleString('uk-UA')}.`
            : 'Відкритої зміни немає.'}</p>
      )}
      <div className="self-attendance__actions">
        <button type="button" disabled={!ready || !enabled || busy || !!openShift}
          onClick={() => void mark('check-in')}>Прийти на роботу</button>
        <button type="button" disabled={!ready || !enabled || busy || !openShift}
          onClick={() => void mark('check-out')}>Піти з роботи</button>
        <button type="button" disabled={busy} onClick={() => void refresh()}>Оновити стан</button>
        <Link to="/schedule">Мій графік і табель →</Link>
      </div>
      <p className="self-attendance__hint">Табель лише обраного ресторану. Вхід у систему не починає зміну й не створює працевлаштування.</p>
    </section>
  );
};

export default SelfAttendance;
